import { ref, reactive, watch } from 'vue'
import type { Ref } from 'vue'
import { type Condition, OP } from '@/lib/watcher'
import { useStageStore } from '@/stores/stage'
import { useSharedWorker } from '@/composables/useSharedworker'
import type { DB } from '@/lib/kysely-d'
import mitt from 'mitt'

const worker = useSharedWorker()

const DELETED = Symbol('deleted')
type StageStateSnapshot = string | typeof DELETED
const stageState: Map<string, StageStateSnapshot> = new Map()

type StageStateEvents = Record<keyof DB, string | typeof DELETED>
const emitter = mitt<StageStateEvents>()

export const useDataTimestamp = () => {
  const stage = useStageStore()
  watch(stage.getAll(), async (store) => {
    for (const [key, entity] of store[Symbol.iterator]()) {
      const prev = stageState.get(key)
      const current = entity.isDeleted ? DELETED : JSON.stringify(await entity.parsed)
      if (prev !== current) {
        stageState.set(key, current)
        emitter.emit(entity.table as keyof DB, current)
      }
    }
  })

  const dbTimestampRef = (lifetime: Promise<void>, conds: Array<Condition>): Ref<number> => {
    const timestamp = ref(Date.now())
    // start observer co-routine
    ;(async () => {
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const modified = await Promise.race([worker.watch(conds), lifetime])
        if (modified !== undefined) {
          timestamp.value = Date.now()
        } else {
          // if lifetime: Promise<void> resolved, stop observer.
          break
        }
      }
    })()
    return timestamp
  }

  const stageTimestampRef = (lifetime: Promise<void>, entities: Array<keyof DB>): Ref<number> => {
    const timestamp = ref(Date.now())
    // mitt requires handler function reference when 'off'
    const handlers: Partial<Record<keyof DB, () => void>> = {}
    for (const table of entities) {
      const f = () => (timestamp.value = Date.now())
      handlers[table] = f
      emitter.on(table, f)
    }
    lifetime.then(() => {
      for (const [table, f] of Object.entries(handlers)) {
        emitter.off(table as keyof DB, f)
      }
    })
    return timestamp
  }

  const timestampRefsOn = (lifetime: Promise<void>, tables: Array<keyof DB>) => {
    const dbTimestamp = dbTimestampRef(
      lifetime,
      tables.flatMap((tbl) => [
        { tbl: tbl, op: OP.SQLITE_DELETE },
        { tbl: tbl, op: OP.SQLITE_INSERT },
        { tbl: tbl, op: OP.SQLITE_UPDATE }
      ])
    )
    const stageTimestamp = stageTimestampRef(lifetime, tables)
    console.debug('timestampRefsOn', dbTimestamp, stageTimestamp, tables)
    return reactive({ dbTimestamp, stageTimestamp })
  }

  return {
    dbTimestampRef,
    stageTimestampRef,
    timestampRefsOn
  }
}
